import React, { useState, useEffect } from 'react'
import { Form, Button, message, PageHeader, Divider } from 'antd'
import SplitShifts from './SplitShifts'
import FulfillmentTypeToggle from './General/FulfillmentTypeToggle'
import TimeSlots, { preorder_pickup_timeslots } from './General/TimeSlots'
import FulfillmentTimesType from './General/FulfillmentTimesType'
import {
  UPDATE_PICKUP_SETTINGS,
  CREATE_SETTINGS,
  UPDATE_PREORDER_PICKUP_SPLIT_HOURS,
  UPDATE_ORDER_TYPE_SETTING_CAPACITY
} from './actions'
import {
  QUERY_STORE_ORDER_TYPE_CAPACITY,
  OrderTypeCapacity
} from 'components/Locations/LocationQueries'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { Schedules, Store, StoreOrderTypeSettings } from '@slerp/controls'
import { initialSchedules } from './utils'
import { sanitizeShifts, WithConflictingDailyShifts } from './SplitShifts/utils'
import isEmpty from 'lodash/isEmpty'
import { uuid } from 'uuidv4'
import CapacityRules from './General/CapacityRules'
import { Store as FormStore } from 'rc-field-form/lib/interface'
import SetupNoticeModal from './Cutoffs/SetupNoticeModal'
import ErrorMessage from 'components/Utils/ErrorMessage'
import { useSession, isAdmin, isManager } from '@slerp/accounts'
import { SplitHour, SplitHourRaw } from './SplitShifts/types'
import MinimumOrderValueInput from '../SameDaySettings/MinimumOrderValueInput'
import AutoAccept from './General/AutoAccept'
import { storeOrderTypeSettingsInitialValue } from '../utils'
import { FormActions } from '../commonStyles'

interface InitialValuesType {
  pickup_timeslots_order_limit: number | null
  products_per_timeslot: number | null
  capacity_per_product: number | null
  capacity_per_modifier: number | null
  auto_accept: StoreOrderTypeSettings | Record<string, unknown>
}

const PickupForm = ({
  store,
  invalidCutoffs,
  refetch
}: {
  store: Store
  invalidCutoffs: boolean
  refetch: () => void
}) => {
  const { user } = useSession()
  const canManage = isAdmin(user) || isManager(user)
  const [showModal, setShowModal] = useState(false)
  const settings =
    store.pre_order_settings?.length > 0 ? store.pre_order_settings[0] : null
  const [pickupEnabled, setPickupEnabled] = useState(
    settings?.pickup_enabled || false
  )
  const [pickupTimes, setPickupTimes] = useState<
    'opening_hours' | 'custom_hours'
  >(settings?.pickup_times || 'opening_hours')

  const getPickupTimeslot = () => {
    if (settings?.fixed_pickup_times_enabled) return 'fixed'
    return settings?.pickup_interval || 15
  }
  const [timeslot, setTimeslot] = useState<string | number>(getPickupTimeslot())

  const [schedules] = useState<Schedules>(
    settings && !isEmpty(settings.pickup_schedule)
      ? settings.pickup_schedule
      : initialSchedules
  )
  const [splitHours, setSplitHours] = useState<SplitHour[]>(
    sanitizeShifts(
      ((settings?.pickup_split_hours as SplitHourRaw[]) || []).filter(
        (shift: SplitHour) => shift.enabled && isEmpty(shift.special_date)
      )
    )
  )
  const [form] = Form.useForm()

  const { data: capacityData, refetch: refetchCapacity } = useQuery(
    QUERY_STORE_ORDER_TYPE_CAPACITY,
    { variables: { store_id: store.id }, fetchPolicy: 'no-cache' }
  )

  useEffect(() => {
    const ots = capacityData?.store_order_type_settings?.find(
      (s: OrderTypeCapacity) =>
        s.fulfillment_type === 'pickup' && s.order_type === 'preorder'
    )
    if (!ots) return
    form.setFieldsValue({
      products_per_timeslot: ots.capacity_limit ?? null,
      capacity_per_product: ots.capacity_product_weight_default ?? null,
      capacity_per_modifier: ots.capacity_modifier_weight_default ?? null
    })
  }, [capacityData])

  const [updateStore, { loading, error }] = useMutation(
    settings ? UPDATE_PICKUP_SETTINGS : CREATE_SETTINGS,
    { fetchPolicy: 'no-cache', awaitRefetchQueries: true }
  )
  const [updatePreorderPickupSplitHours] = useMutation(
    UPDATE_PREORDER_PICKUP_SPLIT_HOURS,
    {
      fetchPolicy: 'no-cache'
    }
  )
  const [updateOrderTypeSettingCapacity] = useMutation(
    UPDATE_ORDER_TYPE_SETTING_CAPACITY,
    { fetchPolicy: 'no-cache' }
  )
  const usingCustomHours = pickupTimes === 'custom_hours'
  const client = useApolloClient()

  const onFinish = (values: FormStore) => {
    form.validateFields().then(() => {
      message.loading('Updating... Please wait.')
      updateStore({
        variables: {
          ...(settings ? {} : { id: uuid() }),
          store_id: store.id,
          pickup_enabled: pickupEnabled,
          fixed_pickup_times_enabled: timeslot === 'fixed',
          pickup_interval:
            timeslot === 'fixed' ? settings?.pickup_interval || 60 : timeslot,
          pickup_times: pickupTimes,
          pickup_schedule: usingCustomHours
            ? schedules
            : settings?.pickup_schedule,
          pickup_timeslots_order_limit:
            values.pickup_timeslots_order_limit ?? null,
          pickup_preorder_mov:
            values.preorder_pickup_minimum_order_value === undefined
              ? store.preorder_pickup_minimum_order_value || 0
              : values.preorder_pickup_minimum_order_value,
          settings: {
            ...store.settings,
            pre_order_pickup: pickupEnabled
          },
          order_type_settings:
            values.auto_accept ??
            (store.store_order_type_settings?.find(
              (sot) =>
                sot.fulfillment_type === 'pickup' &&
                sot.order_type === 'preorder'
            ) ||
              storeOrderTypeSettingsInitialValue(
                store.id,
                'pickup',
                'preorder'
              ))
        }
      })
        .then(() => {
          const tasks: Promise<any>[] = []

          if (usingCustomHours) {
            tasks.push(
              updatePreorderPickupSplitHours({
                variables: { id: store.id, splitHours }
              })
            )
          }

          tasks.push(
            updateOrderTypeSettingCapacity({
              variables: {
                store_id: store.id,
                order_type: 'preorder',
                fulfillment_type: 'pickup',
                capacity_limit: values.products_per_timeslot ?? null,
                capacity_product_weight_default:
                  values.capacity_per_product ?? null,
                capacity_modifier_weight_default:
                  values.capacity_per_modifier ?? null
              }
            })
          )

          return Promise.all(tasks)
        })
        .then(() => {
          message.destroy()
          message.success('Updated pre-order pickup settings', 2)
        })
        .catch((error) => {
          message.destroy()
          message.error(
            `Unable to update your pre-order pickup settings due to: ${error}`,
            5
          )
        })
        .finally(() => {
          refetch()
          refetchCapacity()
        })
    })
  }

  // show warning modal if there is no cut-off settings data and user enables delivery
  if (!settings && pickupEnabled) {
    setShowModal(true)
    setPickupEnabled(false)
  }

  useEffect(() => {
    if (pickupTimes === 'opening_hours') {
      setSplitHours(
        sanitizeShifts(
          ((settings?.pickup_split_hours as SplitHourRaw[]) || []).filter(
            (shift: SplitHour) => shift.enabled && isEmpty(shift.special_date)
          )
        )
      )
    }
  }, [pickupTimes])

  const isSplitShiftsInvalid = () => {
    // Only validate split shifts when using custom hours
    return usingCustomHours && WithConflictingDailyShifts(splitHours)
  }

  const pickupPreorderOTS = store.store_order_type_settings?.find(
    (sot) => sot.fulfillment_type === 'pickup' && sot.order_type === 'preorder'
  )

  const initialValues: InitialValuesType = {
    pickup_timeslots_order_limit:
      settings?.pickup_timeslots_order_limit || null,
    products_per_timeslot: null,
    capacity_per_product: null,
    capacity_per_modifier: null,
    auto_accept:
      pickupPreorderOTS ||
      storeOrderTypeSettingsInitialValue(store.id, 'pickup', 'preorder')
  }

  return (
    <Form
      data-testid='preorder-pickup-settings-form'
      layout='vertical'
      colon={false}
      onFinish={onFinish}
      initialValues={initialValues}
      form={form}
      disabled={!canManage}
    >
      {error && (
        <ErrorMessage
          title='Pickup settings could not be updated'
          subheading={`Error: ${error}`}
        />
      )}
      <PageHeader title='Pickup' className='settings-title' />

      {showModal && (
        <SetupNoticeModal
          title='Please setup your cutoff times before enabling pickup'
          visible={showModal}
          onClose={() => {
            setShowModal(false)
            client.resetStore()
          }}
        />
      )}

      {/* Enable pickup */}
      <FulfillmentTypeToggle
        value={pickupEnabled}
        type='pickup'
        onClick={
          (invalidCutoffs || !settings) && !pickupEnabled
            ? () => setShowModal(true)
            : null
        }
        handleChange={setPickupEnabled}
        disabled={!canManage}
      />
      {pickupEnabled && (
        <>
          <Form.Item label='Pickup times:' className='_mb-40'>
            <FulfillmentTimesType
              value={pickupTimes}
              handleChange={(value) => setPickupTimes(value)}
              disabled={!canManage}
            />
          </Form.Item>
          {usingCustomHours && (
            <SplitShifts
              splitHours={splitHours}
              setSplitHours={(splitHours) => setSplitHours(splitHours)}
              disabled={false}
            />
          )}
          <TimeSlots
            formName='pickup_interval'
            value={timeslot}
            handleChange={(value) => value && setTimeslot(value)}
            timeslots={preorder_pickup_timeslots}
            disabled={!canManage}
          />
          <AutoAccept
            inputName='auto_accept'
            formRef={form}
            isPreOrder={true}
            isDelivery={false}
            disabled={!canManage}
          />
          <MinimumOrderValueInput
            inputName='preorder_pickup_minimum_order_value'
            defaultValue={
              Number(store.preorder_pickup_minimum_order_value) || 0
            }
            disabled={!canManage}
            testId='preorder-pickup-minimum-order-value'
          />
          <Divider />
          <CapacityRules
            orderLimitFieldName='pickup_timeslots_order_limit'
            disabled={!canManage}
          />
        </>
      )}
      <Divider />
      <FormActions>
        <Button
          loading={loading}
          title='Save'
          htmlType='submit'
          className='_center-vertical _ml-auto'
          disabled={!canManage || isSplitShiftsInvalid()}
        >
          {loading ? 'Saving' : 'Save'}
        </Button>
      </FormActions>
    </Form>
  )
}

export default PickupForm
